import React, { useState, useEffect } from 'react';
import { PROJECT_DETAILS } from '../data/mockData';
import {
  X,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Calendar,
  Building2,
  Layers,
  Home,
  Car,
  TreePine,
  Shield,
  ArrowRight,
  CheckCircle,
  Navigation,
  IndianRupee,
  FileText,
} from 'lucide-react';

const ProjectDetail = ({ projectId, isOpen, onClose, onEnquire }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  const project = PROJECT_DETAILS[projectId];
  const handleEnquire = () => {
    if (onEnquire) {
      onEnquire(project);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const nextImage = () => {
    setActiveImageIndex((prev) => (prev + 1) % project.images.length);
  };

  const prevImage = () => {
    setActiveImageIndex(
      (prev) => (prev - 1 + project.images.length) % project.images.length
    );
  };

  const specItems = [
    { icon: Layers, label: 'Land Area', value: project.landArea },
    { icon: Building2, label: 'Towers', value: project.towers },
    { icon: Home, label: 'Total Units', value: project.totalUnits },
    { icon: Layers, label: 'Floors', value: project.floors },
    { icon: TreePine, label: 'Open Space', value: project.openSpace },
    { icon: Car, label: 'Parking', value: project.parking },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'configurations', label: 'Configurations' },
    ...(project.videos && project.videos.length > 0 ? [{ id: 'videos', label: 'Videos' }] : []),
    { id: 'amenities', label: 'Amenities' },
    { id: 'location', label: 'Location' },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Content */}
      <div className="relative w-full max-w-5xl mx-4 my-6 bg-[#111] rounded-xl shadow-2xl border border-gray-800 animate-modal-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-gray-700 flex items-center justify-center text-white hover:bg-[#DAA520] hover:text-black hover:border-[#DAA520] transition-all duration-300"
        >
          <X size={20} />
        </button>

        {/* Image Gallery */}
        <div className="relative h-[320px] sm:h-[420px] overflow-hidden rounded-t-xl">
          <img
            src={project.images[activeImageIndex].url}
            alt={project.images[activeImageIndex].caption}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />

          {/* Image Nav */}
          {project.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#DAA520] hover:text-black transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#DAA520] hover:text-black transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          {/* Thumbnails */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90%] overflow-x-auto px-2 py-1 scrollbar-hide">
            {project.images.map((img, index) => (
              <button
                key={index}
                onClick={() => setActiveImageIndex(index)}
                className={`w-14 h-10 rounded-md overflow-hidden border-2 transition-all flex-shrink-0 ${
                  activeImageIndex === index
                    ? 'border-[#DAA520] opacity-100'
                    : 'border-transparent opacity-60 hover:opacity-90'
                }`}
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Caption & Counter */}
          <div className="absolute bottom-20 left-6 flex items-center gap-3">
            <span className="text-sm text-white/80 bg-black/40 backdrop-blur-sm px-3 py-1 rounded">
              {project.images[activeImageIndex].caption}
            </span>
            <span className="text-xs text-white/60 bg-black/40 backdrop-blur-sm px-2 py-1 rounded">
              {activeImageIndex + 1} / {project.images.length}
            </span>
          </div>
        </div>

        {/* Project Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 bg-[#DAA520] text-black text-xs font-bold tracking-wider rounded-sm">
                  {project.status.toUpperCase()}
                </span>
                <span className="px-3 py-1 bg-[#DAA520]/10 text-[#DAA520] text-xs font-bold tracking-wider rounded-sm border border-[#DAA520]/30">
                  {project.builder.toUpperCase()}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">
                {project.name}
              </h2>
              <p className="text-[#DAA520] text-sm italic mb-2">
                {project.tagline}
              </p>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <MapPin size={14} className="text-[#DAA520]" />
                {project.location}
              </div>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-2xl font-bold text-[#DAA520]">
                {project.priceRange}
              </div>
              <div className="text-gray-500 text-xs mt-1">
                {project.pricePerSqft}
              </div>
              {project.rera && (
                <div className="flex items-center gap-1 text-gray-400 text-xs mt-2">
                  <Shield size={12} className="text-green-500" />
                  RERA: {project.rera.slice(0, 20)}...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Specs Grid */}
        <div className="px-6 sm:px-8 py-4">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {specItems.map((spec, index) => {
              const Icon = spec.icon;
              return (
                <div
                  key={index}
                  className="bg-[#1a1a1a] rounded-lg p-3 text-center border border-gray-800 hover:border-[#DAA520]/30 transition-colors"
                >
                  <Icon size={18} className="text-[#DAA520] mx-auto mb-1" />
                  <div className="text-white text-xs font-semibold">{spec.value}</div>
                  <div className="text-gray-500 text-[10px] mt-0.5">{spec.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 sm:px-8 pt-2">
          <div className="flex gap-1 border-b border-gray-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium tracking-wider transition-all relative ${
                  activeTab === tab.id
                    ? 'text-[#DAA520]'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#DAA520]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 sm:px-8 py-6">
          {/* Overview */}
          {activeTab === 'overview' && (
            <div>
              <p className="text-gray-300 leading-relaxed mb-6">
                {project.description}
              </p>
              <h4 className="text-white font-semibold mb-4 text-sm tracking-wider">
                PROJECT HIGHLIGHTS
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {project.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-[#DAA520] flex-shrink-0" />
                    <span className="text-gray-400 text-sm">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Configurations */}
          {activeTab === 'configurations' && (
            <div>
              <div className="space-y-4">
                {project.configurations.map((config, index) => (
                  <div
                    key={index}
                    className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:border-[#DAA520]/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#DAA520]/10 flex items-center justify-center">
                        <Home size={22} className="text-[#DAA520]" />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-lg">{config.type}</div>
                        {config.subtitle && <div className="text-[#DAA520] text-xs font-medium">{config.subtitle}</div>}
                        <div className="text-gray-400 text-sm">{config.size}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-[#DAA520] font-bold text-lg flex items-center gap-1">
                        <IndianRupee size={16} />
                        {config.price.replace('₹', '')}
                      </div>
                      <button
                        onClick={handleEnquire}
                        className="px-4 py-2 bg-[#DAA520] text-black text-xs font-semibold tracking-wider rounded-sm hover:bg-[#C49B17] transition-colors"
                      >
                        ENQUIRE
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos */}
          {activeTab === 'videos' && project.videos && (
            <div className="space-y-6">
              {project.videos.map((video, index) => (
                <div key={index} className="bg-[#1a1a1a] border border-gray-800 rounded-lg overflow-hidden hover:border-[#DAA520]/30 transition-colors">
                  <video
                    controls
                    className="w-full max-h-[400px] bg-black"
                    poster=""
                    preload="metadata"
                  >
                    <source src={video.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="p-4">
                    <p className="text-gray-300 text-sm font-medium">{video.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Amenities tab */}
          {activeTab === 'amenities' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {project.amenities.map((amenity, index) => (
                <div
                  key={index}
                  className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-3 flex items-center gap-3 hover:border-[#DAA520]/30 transition-colors"
                >
                  <CheckCircle size={14} className="text-[#DAA520] flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{amenity}</span>
                </div>
              ))}
            </div>
          )}

          {/* Location */}
          {activeTab === 'location' && (
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm tracking-wider">
                NEARBY LANDMARKS
              </h4>
              <div className="space-y-3 mb-6">
                {project.nearbyPlaces.map((place, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-[#1a1a1a] border border-gray-800 rounded-lg p-4 hover:border-[#DAA520]/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Navigation size={16} className="text-[#DAA520]" />
                      <span className="text-gray-300 text-sm">{place.name}</span>
                    </div>
                    <span className="text-[#DAA520] text-sm font-semibold">{place.distance}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 sm:px-8 py-5 border-t border-gray-800 flex flex-col sm:flex-row gap-3">
          {project.mapUrl && (
            <a
              href={project.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-600 text-gray-300 font-semibold tracking-wider text-sm rounded-sm hover:border-[#DAA520] hover:text-[#DAA520] transition-all duration-300"
            >
              <MapPin size={16} />
              VIEW ON MAP
            </a>
          )}
          <button
            onClick={handleEnquire}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#DAA520] text-black font-semibold tracking-wider text-sm rounded-sm hover:bg-[#C49B17] transition-all duration-300"
          >
            <FileText size={16} />
            ENQUIRE NOW
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
